/**
 * The one way the SPA moves a listing between draft / published / archived.
 *
 * The three transition procedures were wired up separately on the detail page
 * and the list page, with the same invalidations and near-identical toasts. This
 * wraps them behind the concept they share: pick a **destination**, not a verb.
 * That framing is what lets `archived → draft` exist without the copy lying
 * about it (@see STATUS_DONE).
 *
 * Every visibility change is `store.catalog.publish` — a different permission
 * from the form's `update` (`store.catalog.manage`) — so the check lives here
 * rather than being re-derived at each call site. `canChange` is display only;
 * the router gate is the enforcement.
 */
import { toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { storeErrorMessage } from "../error-message.ts";
import { STATUS_DONE, STATUS_LABEL, type Visibility } from "./listing-status.ts";

export interface ListingVisibilityControls {
  /**
   * Move the listing to `to`. Resolves once it's there; **rejects** (after
   * toasting the reason) when it isn't — so a `ConfirmDialog` awaiting this
   * stays open on failure rather than closing over an error.
   */
  setVisibility: (listingId: string, to: Visibility) => Promise<void>;
  /** True while any of the three transitions is in flight. */
  isPending: boolean;
  /** Does this user hold `store.catalog.publish`? */
  canChange: boolean;
}

export function useListingVisibility(): ListingVisibilityControls {
  const utils = trpc.useUtils();
  const canChange = usePermission("store.catalog.publish");

  // `get` is invalidated without an input, so every cached product refreshes —
  // the detail page and the edit form both read it, and the caller may not be
  // the screen showing the row that moved.
  const settle = (to: Visibility) => {
    void utils.storeCatalog.get.invalidate();
    void utils.storeCatalog.list.invalidate();
    void utils.storeCatalog.counts.invalidate();
    toast.success(STATUS_DONE[to]);
  };

  const publish = trpc.storeCatalog.publish.useMutation();
  const unpublish = trpc.storeCatalog.unpublish.useMutation();
  const archive = trpc.storeCatalog.archive.useMutation();

  const setVisibility = async (listingId: string, to: Visibility) => {
    // `archive` sets `archived` from any state in one write, so a published
    // product is archived directly rather than unpublished first — one
    // transition, one audit entry, `{ from: "published" }`.
    const run =
      to === "published" ? publish : to === "archived" ? archive : unpublish;
    try {
      await run.mutateAsync({ listingId });
      settle(to);
    } catch (e) {
      toast.error(`Couldn't move to ${STATUS_LABEL[to].toLowerCase()}`, {
        description: storeErrorMessage(e),
      });
      throw e;
    }
  };

  return {
    setVisibility,
    isPending: publish.isPending || unpublish.isPending || archive.isPending,
    canChange,
  };
}
